import React, { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2, Mic, Square, RotateCcw, Trophy, MessageSquareQuote, ShieldCheck, PhoneCall,
  ChevronLeft, CheckCircle2, Lightbulb, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const DOMAIN_META = {
  AML: { label: "Financial Crime (AML)", icon: ShieldCheck },
  AR: { label: "AR Calling", icon: PhoneCall },
};

function ScoreBar({ label, value }) {
  return (
    <div data-testid={`score-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex justify-between text-xs font-mono mb-1">
        <span className="text-muted-foreground uppercase tracking-wider">{label}</span>
        <span className="font-bold">{value}</span>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden">
        <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function DrillRunner({ drill, onBack, onDone }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const reset = () => { setAudioBlob(null); setAudioUrl(null); setResult(null); setSeconds(0); };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setSeconds(0);
      setResult(null);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      toast.error("Microphone access denied. Please allow mic permission.");
    }
  };

  const stop = () => {
    mediaRef.current?.stop();
    clearInterval(timerRef.current);
    setRecording(false);
  };

  const analyze = async () => {
    if (!audioBlob) return;
    setAnalyzing(true);
    const fd = new FormData();
    fd.append("audio", audioBlob, "recording.webm");
    try {
      const token = localStorage.getItem("pm_token");
      const res = await fetch(`${API}/voice-drills/${drill.id}/submit`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
        body: fd,
      });
      if (!res.ok) throw new Error("failed");
      const json = await res.json();
      setResult(json.result);
      onDone && onDone();
      toast.success("AI feedback ready!");
    } catch {
      toast.error("Could not analyze recording. Try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  const Icon = (DOMAIN_META[drill.domain] || DOMAIN_META.AML).icon;

  return (
    <div className="max-w-3xl" data-testid="drill-runner">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-5">
        <ChevronLeft className="w-4 h-4" /> All drills
      </button>

      <div className="rounded-md border border-border bg-card p-6 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider px-2 py-1 rounded bg-primary/10 text-primary">
            <Icon className="w-3.5 h-3.5" /> {(DOMAIN_META[drill.domain] || {}).label}
          </span>
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{drill.difficulty}</span>
        </div>
        <h2 className="font-heading text-2xl font-black tracking-tight mb-3">{drill.title}</h2>
        <p className="text-sm text-muted-foreground mb-3"><span className="font-semibold text-foreground">Your role:</span> {drill.role}</p>
        <p className="text-sm text-muted-foreground mb-4">{drill.scenario}</p>
        <div className="p-4 rounded-md bg-secondary/50 border-l-2 border-primary mb-4">
          <p className="text-[15px] italic" data-testid="drill-prompt-line">{drill.prompt_line}</p>
        </div>
        <div className="flex items-start gap-2 text-sm">
          <MessageSquareQuote className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <p><span className="font-semibold">Speak your response:</span> {drill.task}</p>
        </div>
      </div>

      <div className="rounded-md border border-border bg-card p-6 flex flex-col items-center text-center">
        {!recording && !audioBlob && (
          <Button onClick={start} className="rounded-full h-16 w-16 p-0" data-testid="drill-record-btn">
            <Mic className="w-6 h-6" />
          </Button>
        )}
        {recording && (
          <>
            <div className="font-mono text-2xl mb-3 text-primary">{String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}</div>
            <Button onClick={stop} variant="destructive" className="rounded-full h-16 w-16 p-0 animate-pulse" data-testid="drill-stop-btn">
              <Square className="w-6 h-6" />
            </Button>
          </>
        )}
        {audioBlob && !recording && (
          <div className="w-full space-y-4">
            <audio src={audioUrl} controls className="w-full" data-testid="drill-audio-playback" />
            <div className="flex gap-3 justify-center">
              <Button onClick={reset} variant="outline" className="rounded-md gap-2" data-testid="drill-redo-btn">
                <RotateCcw className="w-4 h-4" /> Re-record
              </Button>
              <Button onClick={analyze} disabled={analyzing} className="rounded-md gap-2" data-testid="drill-analyze-btn">
                {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" /> Grading…</> : <><Sparkles className="w-4 h-4" /> Get AI feedback</>}
              </Button>
            </div>
          </div>
        )}
        {!recording && !audioBlob && <p className="text-sm text-muted-foreground mt-3">Tap the mic and role-play your spoken reply.</p>}
      </div>

      {result && (
        <div className="rounded-md border border-border bg-card p-6 mt-4 fade-in-up" data-testid="drill-result">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <Trophy className="w-7 h-7 text-primary" />
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Overall</div>
              <div className="font-mono text-3xl font-black" data-testid="drill-overall-score">{result.overall}<span className="text-lg text-muted-foreground">/100</span></div>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mb-5">
            <ScoreBar label="Communication" value={result.communication} />
            <ScoreBar label="Relevance" value={result.relevance} />
            <ScoreBar label="Domain Accuracy" value={result.domain_accuracy} />
          </div>
          <p className="text-sm leading-relaxed mb-4">{result.feedback}</p>

          {result.strengths?.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-mono uppercase tracking-wider text-success mb-1.5">Strengths</div>
              {result.strengths.map((s, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-muted-foreground mb-1"><CheckCircle2 className="w-4 h-4 text-success shrink-0 mt-0.5" />{s}</div>
              ))}
            </div>
          )}
          {result.improvements?.length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-mono uppercase tracking-wider text-primary mb-1.5">To improve</div>
              {result.improvements.map((s, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-muted-foreground mb-1"><Lightbulb className="w-4 h-4 text-primary shrink-0 mt-0.5" />{s}</div>
              ))}
            </div>
          )}
          {result.model_answer && (
            <div className="p-4 rounded-md bg-secondary/50 mb-4">
              <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">Model answer</div>
              <p className="text-sm italic">{result.model_answer}</p>
            </div>
          )}
          {result.transcript && (
            <details className="text-sm">
              <summary className="cursor-pointer text-muted-foreground font-mono text-xs uppercase tracking-wider">Your transcript</summary>
              <p className="mt-2 text-muted-foreground">{result.transcript}</p>
            </details>
          )}
          <div className="mt-5 flex gap-3">
            <Button onClick={reset} variant="outline" className="rounded-md gap-2" data-testid="drill-tryagain-btn"><RotateCcw className="w-4 h-4" /> Try again</Button>
            <Button onClick={onBack} className="rounded-md" data-testid="drill-next-btn">Pick another drill</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function VoiceDrills() {
  const [domain, setDomain] = useState("All");
  const [active, setActive] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["voice-drills"],
    queryFn: async () => (await api.get("/voice-drills")).data,
  });
  const { data: histData, refetch } = useQuery({
    queryKey: ["voice-drill-results"],
    queryFn: async () => (await api.get("/voice-drills/results")).data,
  });

  const drills = data?.drills || [];
  const filtered = domain === "All" ? drills : drills.filter((d) => d.domain === domain);
  const history = histData?.results || [];

  if (active) return <DrillRunner drill={active} onBack={() => setActive(null)} onDone={refetch} />;

  return (
    <div className="space-y-6" data-testid="voice-drills-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">Speaking · AI Graded</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter flex items-center gap-3">
          <MessageSquareQuote className="w-8 h-8 text-primary" /> Scenario Voice Drills
        </h1>
        <p className="text-muted-foreground mt-1">Role-play real AML & AR-calling situations out loud — the AI grades your communication, relevance and domain handling.</p>
      </div>

      <Tabs value={domain} onValueChange={setDomain}>
        <TabsList data-testid="drill-domain-tabs">
          <TabsTrigger value="All" data-testid="tab-All">All</TabsTrigger>
          <TabsTrigger value="AML" data-testid="tab-AML">Financial Crime</TabsTrigger>
          <TabsTrigger value="AR" data-testid="tab-AR">AR Calling</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {filtered.map((d) => {
            const Icon = (DOMAIN_META[d.domain] || DOMAIN_META.AML).icon;
            const best = history.filter((h) => h.drill_id === d.id).reduce((m, h) => Math.max(m, h.overall || 0), null);
            return (
              <button
                key={d.id}
                onClick={() => setActive(d)}
                className="text-left p-5 rounded-md border border-border bg-card hover:border-primary transition-colors group"
                data-testid={`drill-card-${d.id}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-primary/10 text-primary">
                    <Icon className="w-3 h-3" /> {(DOMAIN_META[d.domain] || {}).label}
                  </span>
                  <span className="text-xs font-mono uppercase text-muted-foreground">{d.difficulty}</span>
                  {best !== null && <span className="ml-auto text-xs font-mono text-success">Best {best}</span>}
                </div>
                <h3 className="font-heading font-bold text-lg mb-1 group-hover:text-primary transition-colors">{d.title}</h3>
                <p className="text-sm text-muted-foreground line-clamp-2">{d.scenario}</p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
